import { useSelector } from "react-redux";
import { AuthEnums } from "models/enums";
import { AppState } from "store";
import { hasRelationFlag } from "utils/helpers/relationFlagHelper";

function useCheckRelations() {
  const relationFlags = useSelector((store: AppState) => store.auth.selectedCompany.extendedRelationFlags);
  const isAuditorAssistant = useSelector(
    (store: AppState) => store.auth.selectedCompany.loggedInUserIsAuditorAssistant
  );

  const hasRelation = (
    personCompanyRelationType: AuthEnums.PersonCompanyRelationType,
    ignoreAdmin: boolean = false
  ) => {
    const adminOverrides =
      hasRelationFlag(relationFlags, AuthEnums.PersonCompanyRelationType.Admin) &&
      !ignoreAdmin &&
      personCompanyRelationType !== AuthEnums.PersonCompanyRelationType.Auditor;
    return adminOverrides || hasRelationFlag(relationFlags, personCompanyRelationType);
  };

  const hasRelations = (
    personCompanyRelationTypes: AuthEnums.PersonCompanyRelationType[],
    ignoreAdmin: boolean = false
  ) => {
    const adminOverrides =
      hasRelationFlag(relationFlags, AuthEnums.PersonCompanyRelationType.Admin) &&
      !ignoreAdmin &&
      personCompanyRelationTypes.every((x) => x !== AuthEnums.PersonCompanyRelationType.Auditor);
    return (
      adminOverrides || personCompanyRelationTypes.some((x) => hasRelationFlag(relationFlags, x))
    );
  };

  const getIsAdmin = () => hasRelation(AuthEnums.PersonCompanyRelationType.Admin);
  const getIsAuditor = () => hasRelation(AuthEnums.PersonCompanyRelationType.Auditor);
  const getIsRevisor = () => hasRelation(AuthEnums.PersonCompanyRelationType.Revisor, true);
  const getIsReadonlyUser = () => getIsAuditor() || getIsRevisor();
  const getIsBoard = () => hasRelation(AuthEnums.PersonCompanyRelationType.Board);
  const getIsAuditorAssistant = () => !!isAuditorAssistant;

  return {
    hasRelation,
    hasRelations,
    getIsAdmin,
    /**
     * Checks whether the user has ReadOnly permission
     */
    getIsAuditor,
    /**
     * Checks whether the user has Revisor permission
     * (Revisor: New permission specially designed for Auditors)
     */
    getIsRevisor,
    /**
     * Checks whether the user should have a readonly mode
     */
    getIsReadonlyUser,
    /**
     * Checks whether the user is a board member
     */
    getIsBoard,
    /**
     * Checks whether the user is an auditor assistant on an annual report of the company
     * (assistants have no Revisor relation)
     */
    getIsAuditorAssistant,
  };
}
export default useCheckRelations;
